import { DecimalPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';
import { IconUnknownComponent } from '@components/icon-unknown/icon-unknown.component';
import { SlotRarityOutlineComponent } from '@components/slot-rarity-outline/slot-rarity-outline.component';
import { TooltipItemPreviewComponent } from '@components/tooltip-item-preview/tooltip-item-preview.component';
import { getEntry } from '@helpers/content/content';
import {
  recipeBackdropSprite,
  recipeResultContent,
  recipeResultSpritesheet,
} from '@helpers/crafting/recipes';
import { getMaterialQuantity } from '@helpers/item';
import { resolveRewardDisplay } from '@helpers/item/item-preview';
import { assertNeverReward } from '@helpers/item/loot';
import { isRewardDiscovered } from '@helpers/world-node/world-node-rewards';
import type {
  CollectibleContent,
  DropRarity,
  DroppedReward,
  EquipmentContent,
  ItemContent,
  RecipeContent,
  TradeskillContent,
  WorkerContent,
} from '@interfaces';
import { TippyDirective } from '@ngneat/helipopper';

type RewardContent = {
  name: string;
  description: string;
  sprite: string;
  rarity: DropRarity;
};

@Component({
  selector: 'app-slot-completion-reward',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IconUnknownComponent,
    TippyDirective,
    SlotRarityOutlineComponent,
    TooltipItemPreviewComponent,
    DecimalPipe,
  ],
  templateUrl: './slot-completion-reward.component.html',
  styleUrl: './slot-completion-reward.component.scss',
})
export class SlotCompletionRewardComponent {
  public reward = input.required<DroppedReward>();
  public rewardText = input<string>();
  public rewardTextSmall = input<string>();

  // A recipe has no sprite/rarity of its own - it borrows whatever it
  // crafts, so its spritesheet/content resolve through the recipe's result.
  private recipeContent = computed<RecipeContent | undefined>(() => {
    const reward = this.reward();
    if (!('recipeId' in reward)) return undefined;
    return getEntry<RecipeContent>(reward.recipeId);
  });

  public spritesheet = computed<
    'item' | 'equipment' | 'collectible' | 'worker'
  >(() => {
    const reward = this.reward();

    switch (reward.kind) {
      case 'Item':
        return 'item';
      case 'Equipment':
        return 'equipment';
      case 'Worker':
        return 'worker';
      case 'Recipe': {
        const recipe = this.recipeContent();
        return recipe ? recipeResultSpritesheet(recipe) : 'item';
      }
      case 'Collectible':
        return 'collectible';
      default:
        return assertNeverReward(reward);
    }
  });

  // Composited behind the result sprite for recipe rewards only - the visual
  // cue that this slot grants a recipe, not the crafted item itself.
  public backdropSprite = computed<string | undefined>(() => {
    const reward = this.reward();
    return 'recipeId' in reward ? recipeBackdropSprite() : undefined;
  });

  public content = computed<RewardContent | undefined>(() => {
    const reward = this.reward();
    switch (reward.kind) {
      case 'Item':
        return getEntry<ItemContent>(reward.itemId);
      case 'Equipment':
        return getEntry<EquipmentContent>(reward.equipmentId);
      case 'Worker': {
        const worker = getEntry<WorkerContent>(reward.workerId);
        // Workers have no rarity of their own - displayed as Rare, matching
        // how rare a worker rescue actually is.
        return worker ? { ...worker, rarity: 'Rare' as DropRarity } : undefined;
      }
      case 'Recipe': {
        const recipe = this.recipeContent();
        const result = recipe ? recipeResultContent(recipe) : undefined;
        // The recipe's own name (not its crafted result's) - a recipe reward
        // grants the blueprint, not the item, and recipe names already carry
        // a "Category: Item" naming convention that calls this out.
        return recipe && result ? { ...result, name: recipe.name } : undefined;
      }
      case 'Collectible':
        return getEntry<CollectibleContent>(reward.collectibleId);
      default:
        return assertNeverReward(reward);
    }
  });

  public displayTooltip = computed(() => resolveRewardDisplay(this.reward()));

  public isDiscovered = computed(() => isRewardDiscovered(this.reward()));

  public quantityOfMaterial = computed(() => {
    const reward = this.reward();
    if (reward.kind !== 'Item') return 0;

    return getMaterialQuantity(reward.itemId);
  });

  public unknownTooltip = computed(() => {
    const reward = this.reward();
    switch (reward.kind) {
      case 'Recipe': {
        const recipe = this.recipeContent();
        if (!recipe) return 'A reward yet to be discovered.';

        const tradeskillName = getEntry<TradeskillContent>(
          recipe.tradeskillId,
        )?.name;
        if (!tradeskillName) return 'A reward yet to be discovered.';

        return `A ${tradeskillName} recipe yet to be discovered, craftable at Lv. ${recipe.minTradeskillLevel}.`;
      }

      default: {
        return 'A reward yet to be discovered';
      }
    }
  });
}
